"use client";
import React, { lazy, Suspense, useState, useEffect } from 'react';
import Link from 'next/link';
import { gqlRequest, HOME_FEED_QUERY } from '@/app/graphql';
import Loader from '@/app/components/loader/loader';
import LazyComponent from '@/app/components/LazyComponent';
import './SectionsSidebar.css';

// Lazy load components
const LazyLatestChallengeParticipants = lazy(() => import('@/app/components/LatestChallengeParticipants'));
const LazyDailyQuote = lazy(() => import('@/app/components/dailyQuote/dailyQuote').then(module => ({ default: module.DailyQuote })));
const LazyEmblaCarousel = lazy(() => import('@/app/components/sliders/embala').then(module => ({ default: module.EmblaCarousel })));

export default function SectionsSidebar() {
  const [graphQLData, setGraphQLData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        const data = await gqlRequest({
          query: HOME_FEED_QUERY,
          signal: controller.signal,
        });
        setGraphQLData(data);
        setLoading(false);
      } catch (e) {
        if (e?.name === "CanceledError") return;
        console.log('Sidebar data error:', e);
        setLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, []);

  if (loading) {
    return (
      <aside className="sections-sidebar col-12 col-lg-4 col-xl-3 mb-4">
        <div className="sticky-top" style={{ top: '20px' }}>
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <div className="placeholder-glow">
                <div className="placeholder col-8 mb-2"></div>
                <div className="placeholder col-10 mb-2"></div>
                <div className="placeholder col-6"></div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="sections-sidebar d-none d-md-block col-12 col-lg-4 col-xl-3 mb-4">
      <div className="sticky-top" style={{ top: '20px' }}>
        {/* التحدي الحالي */}
        {/* <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <LazyComponent
              fallback={<Loader />}
              threshold={0.1}
              rootMargin="100px"
            >
              <Suspense fallback={<Loader />}>
                <LazyLatestChallengeParticipants data={graphQLData?.challenges?.nodes} />
              </Suspense>
            </LazyComponent>
          </div>
        </div> */}

        {/* اقتباس اليوم */}
        {/* {graphQLData?.quotes?.nodes && graphQLData.quotes.nodes.length > 0 && (
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <LazyComponent
                fallback={<Loader />}
                threshold={0.1}
                rootMargin="100px"
              >
                <Suspense fallback={<Loader />}>
                  <LazyDailyQuote data={graphQLData.quotes.nodes} />
                </Suspense>
              </LazyComponent>
            </div>
          </div>
        )} */}

        {/* روابط سريعة للأقسام */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <h5 className="card-title mb-3">
              <i className="bi bi-grid-3x3-gap me-2"></i>
              الأقسام
            </h5>
            <div className="d-grid gap-2">
              <Link href="/sections/articles" className="btn btn-outline-dark btn-sm text-start">
                <i className="bi bi-pencil me-2"></i>
                المقالات
              </Link>
              <Link href="/sections/challenges" className="btn btn-outline-dark btn-sm text-start">
                <i className="bi bi-trophy me-2"></i>
                التحديات
              </Link>
              <Link href="/sections/sessions" className="btn btn-outline-dark btn-sm text-start">
                <i className="bi bi-play-btn me-2"></i>
                الجلسات
              </Link>
              <Link href="/sections/podcasts" className="btn btn-outline-dark btn-sm text-start">
                <i className="bi bi-mic me-2"></i>
                البودكاست
              </Link>
              <Link href="/sections/jobs" className="btn btn-outline-dark btn-sm text-start">
                <i className="bi bi-briefcase me-2"></i>
                الوظائف
              </Link>
              <Link href="/sections/plugins" className="btn btn-outline-dark btn-sm text-start">
                <i className="bi bi-puzzle me-2"></i>
                الإضافات
              </Link>
              <Link href="/sections/templates" className="btn btn-outline-dark btn-sm text-start">
                <i className="bi bi-file-earmark-text me-2"></i>
                القوالب
              </Link>
            </div>
          </div>
        </div>

        {/* أحدث المقالات */}
        {graphQLData?.posts?.nodes && graphQLData.posts.nodes.length > 0 && (
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">
                  <i className="bi bi-newspaper me-2"></i>
                  أحدث المقالات
                </h5>
                <Link href="/sections/articles" className="text-decoration-none small">
                  <i className="bi bi-chevron-left ms-1"></i>
                </Link>
              </div>
              <div className="list-group list-group-flush">
                {graphQLData.posts.nodes.slice(0, 5).map((post, index) => (
                  <Link
                    key={post.postId || index}
                    href={`/sections/articles/${post.id}`}
                    className="list-group-item list-group-item-action border-0 px-0 py-2"
                  >
                    <div className="d-flex align-items-start">
                      {post.featuredImage?.node?.sourceUrl && (
                        <img
                          src={post.featuredImage.node.sourceUrl}
                          alt={post.title}
                          className="rounded me-2"
                          style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                        />
                      )}
                      <div className="flex-grow-1">
                        <h6 className="mb-1 small fw-semibold lh-base">{post.title}</h6>
                        {post.date && (
                          <small className="text-muted">
                            <i className="bi bi-calendar3 me-1"></i>
                            {new Date(post.date).toLocaleDateString('ar-EG')}
                          </small>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* أحدث الوظائف */}
        {graphQLData?.jobs?.nodes && graphQLData.jobs.nodes.length > 0 && (
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">
                  <i className="bi bi-briefcase me-2"></i>
                  الوظائف 
                </h5>
                <Link href="/sections/jobs" className="text-decoration-none small">
                  <i className="bi bi-chevron-left ms-1"></i>
                </Link>
              </div>
              <div className="list-group list-group-flush">
                {graphQLData.jobs.nodes.slice(0, 3).map((job, index) => (
                  <Link
                    key={job.jobId || index}
                    href={`/sections/jobs/${job.id}`}
                    className="list-group-item list-group-item-action border-0 px-0 py-2"
                  >
                    <h6 className="mb-1 small fw-semibold lh-base">{job.title}</h6>
                    {job.date && (
                      <small className="text-muted">
                        <i className="bi bi-calendar3 me-1"></i>
                        {new Date(job.date).toLocaleDateString('ar-EG')}
                      </small>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* روابط مفيدة */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <h5 className="card-title mb-3">
              <i className="bi bi-link-45deg me-2"></i>
              روابط مفيدة
            </h5>
            <div className="d-grid gap-2">
              {/* <Link href="/services/quizzes" className="btn btn-outline-secondary btn-sm text-start">
                <i className="bi bi-clipboard-check me-2"></i>
                الاختبارات
              </Link> */}
              <Link href="/services/short" className="btn btn-outline-secondary btn-sm text-start">
                <i className="bi bi-scissors me-2"></i>
                اختصار الروابط
              </Link>
              <Link href="/services/calc" className="btn btn-outline-secondary btn-sm text-start">
                <i className="bi bi-calculator me-2"></i>
                حساب قيمة المشاريع
              </Link>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
